/**
 * Central axios instance used for every backend call.
 *
 * Reads from `REACT_APP_BACKEND_URL` (CRA convention). All routes live
 * under `/api` to satisfy the Kubernetes ingress rule that routes
 * `/api/*` traffic to port 8001, so we build the root URL as
 * `REACT_APP_BACKEND_URL + /api`.
 *
 * `withCredentials: true` lets the httpOnly `access_token` / `refresh_token`
 * cookies set by the backend auth router flow through browser requests.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  // Visible early, before the rest of the app mounts.
  // eslint-disable-next-line no-console
  console.warn("REACT_APP_BACKEND_URL is not set -- API calls will fail.");
}

export const API_BASE = `${BACKEND_URL ?? ""}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/** Convenience helper so UI code can render `response.data.detail` cleanly. */
export function extractApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    // Backend's global RequestValidationError handler (main.py) reshapes
    // every 422 into `{ detail: "Please fix the highlighted fields", fields:
    // {...} }`. `detail` alone is too generic for a single banner message,
    // so for callers that haven't migrated to `parseApiError()` (which maps
    // `fields` onto per-field UI), summarise the field messages here instead
    // -- otherwise every un-migrated form would regress to a vague banner
    // on validation errors that used to show the specific Pydantic message.
    if (data && typeof data === "object" && data.fields && typeof data.fields === "object") {
      const messages = Object.values(data.fields).filter(
        (m): m is string => typeof m === "string",
      );
      if (messages.length > 0) return messages.join(" ");
    }
    const detail = data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((e) => {
          if (e && typeof e.msg === "string") {
            // Pydantic 2 prefixes field_validator errors with "Value error, ".
            return e.msg.replace(/^Value error,\s*/, "");
          }
          return JSON.stringify(e);
        })
        .join(" ");
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}

/** Result of parsing any API error into a form-ready shape. */
export interface ParsedApiError {
  /** Human summary safe to show in a toast/banner. */
  message: string;
  /** Per-field messages, keyed by field name -- empty when the backend
   * didn't return field-level detail (e.g. a plain 403/404/409). */
  fieldErrors: Record<string, string>;
}

/**
 * Parses an error from a POST/PATCH/PUT call into `{ message, fieldErrors }`.
 *
 * Backend's global `RequestValidationError` handler (main.py) reshapes every
 * 422 into `{ detail: "Please fix the highlighted fields", fields: {...} }`.
 * This recognises that shape and lifts `fields` out for forms to map onto
 * their inline per-field error state. Any other error shape (a plain string
 * `detail` from a 400/403/404/409, a network error, etc.) falls back to
 * `extractApiError` with an empty `fieldErrors` map.
 *
 * Forms should always go through this rather than rendering `err.response
 * .data.detail` directly, so a raw Pydantic message never reaches a user.
 */
export function parseApiError(err: unknown): ParsedApiError {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (data && typeof data === "object" && data.fields && typeof data.fields === "object") {
      return {
        message:
          typeof data.detail === "string" ? data.detail : "Please fix the highlighted fields",
        fieldErrors: data.fields as Record<string, string>,
      };
    }
  }
  return { message: extractApiError(err), fieldErrors: {} };
}
